import { useEffect, useRef } from "react";
import { subscribe } from "./liveRefresh";

/**
 * Keep a view live so no screen needs a manual refresh button.
 *
 * Three triggers, each covering a different way the data can go stale:
 *
 *   1. a write anywhere in the app — the API client announces every
 *      successful save, approval, upload and delete, and this screen
 *      refetches at once, in its own tab and in every other tab of this
 *      browser. This is the one that matters: the data moves the moment it
 *      changes instead of waiting for a clock.
 *   2. every `intervalMs` while the tab is visible — the backstop for a
 *      change made from another machine, which no browser event can see.
 *   3. immediately when the window regains focus or the tab is switched
 *      back, so the very first thing you see after Alt-Tab is already current.
 *
 * Polling stops while the tab is hidden, and an invalidation is ignored while
 * hidden too — a screen left open overnight must not hammer the API for
 * numbers nobody is looking at. Becoming visible again reloads once anyway.
 *
 * `fn` is held in a ref and read at call time, so the closure always sees the
 * latest filters/date range without re-subscribing the timer every keystroke.
 *
 * Re-runs within `COALESCE_MS` of the previous one are dropped: `focus` and
 * `visibilitychange` both fire on a tab switch, and a returning user should
 * see one reload, not two. Invalidations are debounced separately by
 * `INVALIDATE_MS`, so a burst of five saves across the app settles into a
 * single refetch rather than five.
 */
const COALESCE_MS = 800;
const INVALIDATE_MS = 250;

export default function useAutoRefresh(fn, intervalMs = 30000) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!intervalMs) return undefined;
    let timer = null;
    let pending = null;
    let lastRun = 0;

    const run = (force) => {
      const now = Date.now();
      if (!force && now - lastRun < COALESCE_MS) return;
      lastRun = now;
      try {
        const r = fnRef.current();
        if (r && typeof r.catch === "function") r.catch(() => {});
      } catch (_) {
        /* a failed background refresh must never surface an error to the user */
      }
    };

    const start = () => {
      if (timer === null) timer = setInterval(() => run(false), intervalMs);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        run(true);
        start();
      } else {
        stop();
      }
    };
    const onFocus = () => run(true);

    // Something was written somewhere. Debounced so a burst collapses to one
    // refetch, and skipped while hidden because nobody is looking at it.
    const onInvalidate = () => {
      if (document.visibilityState !== "visible") return;
      if (pending !== null) clearTimeout(pending);
      pending = setTimeout(() => {
        pending = null;
        run(true);
      }, INVALIDATE_MS);
    };

    const unsubscribe = subscribe(onInvalidate);

    if (document.visibilityState === "visible") start();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stop();
      if (pending !== null) clearTimeout(pending);
      unsubscribe();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs]);
}
